import { yup } from '@/libs/yup';
import moment from 'moment';
import { SPECIFIED_WORKS, japaneseOrdinal } from '@/consts/regular_health_checkup/specified_work';

const specifiedWorkSummarySchema = (label: string) =>
  yup
    .object({
      count: yup.string().matches(/^\d{1,4}$/, {
        message: () => `${label}欄は半角数字4文字以内で入力してください`,
        excludeEmptyString: true
      })
    })
    .notRequired();

const resultSummarySchema = yup
  .object({
    totalCount: yup
      .string()
      .matches(/^\d{1,5}$/, {
        message: ({ label }) => `${label}は半角数字5文字以内で入力してください`,
        excludeEmptyString: true
      })
      .label('実施者数'),
    hasFindingCount: yup
      .string()
      .matches(/^\d{1,4}$/, {
        message: ({ label }) => `${label}は半角数字4文字以内で入力してください`,
        excludeEmptyString: true
      })
      .label('有所見者数')
  })
  .notRequired();

export const schema = yup.object().shape(
  {
    title: yup.string().required().label('報告書の名称'),
    officeId: yup.string().label('適用事業所'),
    officeSnapshot: yup.object({
      name: yup.string(),
      postcode0: yup.string(),
      postcode1: yup.string(),
      prefectureId: yup.string(),
      city: yup.string(),
      houseNumber: yup.string(),
      building: yup.string(),
      phoneNumber0: yup.string(),
      phoneNumber1: yup.string(),
      phoneNumber2: yup.string(),
      laborInsuranceNumber0: yup.string(),
      laborInsuranceNumber1: yup.string(),
      laborInsuranceNumber2: yup.string(),
      laborInsuranceNumber3: yup.string(),
      laborInsuranceNumber4: yup.string(),
      repPositionName: yup.string(),
      repLastName: yup.string(),
      repFirstName: yup.string(),
      laborStandardsInspectionOfficeLShortName: yup.string()
    }),
    businessTypeId: yup.string().label('事業の種類'),
    startOn: yup
      .string()
      .dateFormat()
      .when('endOn', {
        is: endOn => endOn,
        then: schema => schema.required(({ label }) => `${label}の日付を入力してください`)
      })
      .label('報告期間の始点'),
    endOn: yup
      .string()
      .dateFormat()
      .when('startOn', {
        is: startOn => startOn,
        then: schema => schema.required(({ label }) => `${label}の日付を入力してください`)
      })
      .test(
        'afterStartOn',
        ({ label }) => `${label}は始点以降の日付を入力してください`,
        (value: string | undefined, context) => {
          if (!value || !context.parent.startOn) return true;
          const startOn = moment(context.parent.startOn, 'YYYY/MM/DD');
          const endOn = moment(value, 'YYYY/MM/DD');
          return endOn.isSameOrAfter(startOn);
        }
      )
      .label('報告期間の終点'),
    reportNumber: yup
      .string()
      .matches(/^\d{1,2}$/, {
        message: ({ label }) => `${label}は半角数字2文字以内で入力してください`,
        excludeEmptyString: true
      })
      .label('報告回数'),
    aggregationOfficeIds: yup.array(yup.string().required()).label('集計に含める事業所'),
    healthCheckupInstitutionName: yup.string().nullable().label('健康診断実施機関名称'),
    healthCheckupInstitutionLocation: yup.string().nullable().label('健康診断実施機関所在地'),
    healthCheckupInstitutionsCount: yup.number().nonNullable(),
    specifiedWorkSummaries: yup.object(
      Object.fromEntries(
        SPECIFIED_WORKS.map(({ code, label }, index) => [
          code,
          specifiedWorkSummarySchema(`${japaneseOrdinal(index)}　${label}`)
        ])
      )
    ),
    resultSummaries: yup.object({
      hearing1000Hz: resultSummarySchema.label('聴力検査（オージオメーターによる検査）(1000Hz)'),
      hearing4000Hz: resultSummarySchema.label('聴力検査（オージオメーターによる検査）(4000Hz)'),
      hearingOther: resultSummarySchema.label('聴力検査（その他の方法による検査）'),
      chestXRay: resultSummarySchema.label('胸部エックス線検査'),
      sputumTest: resultSummarySchema.label('喀痰検査'),
      bloodPressure: resultSummarySchema.label('血圧'),
      anemiaTest: resultSummarySchema.label('貧血検査'),
      liverFunctionTest: resultSummarySchema.label('肝機能検査'),
      bloodLipidTest: resultSummarySchema.label('血中脂質検査'),
      bloodSugarTest: resultSummarySchema.label('血糖検査'),
      urineSugar: resultSummarySchema.label('尿検査（糖）'),
      urineProtein: resultSummarySchema.label('尿検査（蛋白）'),
      electrocardiogram: resultSummarySchema.label('心電図検査')
    }),
    physicianName: yup.string().nullable().max(30).label('氏名'),
    physicianInstitutionName: yup.string().nullable().max(80).label('所属機関名'),
    physicianInstitutionAddress: yup.string().nullable().max(65).label('所在地'),
    submitOn: yup.string().dateFormat().label('提出年月日'),
    enrolledEmployeesCount: yup
      .string()
      .matches(/^\d{1,5}$/, {
        message: ({ label }) => `${label}は半角数字5文字以内で入力してください`,
        excludeEmptyString: true
      })
      .label('在籍労働者数'),
    checkedEmployeesCount: yup
      .string()
      .matches(/^\d{1,5}$/, {
        message: ({ label }) => `${label}は半角数字5文字以内で入力してください`,
        excludeEmptyString: true
      })
      .label('受診労働者数')
  },
  [['startOn', 'endOn']]
);

export type Schema = yup.InferType<typeof schema>;
